
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import OrderDetailsSummary from './components/OrderdetailsSummary';
import Orderinfo from './components/Orderinfo';
import BillSummary from './components/BillSummary';
import { ApiService } from '@/shared/service/Apiservice';
import { ProfileService } from '../service/Profile.service';
import axios from 'axios';
import { Order } from '../dto/Profile.dto';
import './styles/viewOrderDetails.css';
import LoadingSpinner from '@/shared/components/LodingSpiner/LodingSpiner';

interface OrderDetailsProps {
  orderId?: string | null;
  onBack?: () => void;
}

const ViewOrderDetails: React.FC<OrderDetailsProps> = ({ orderId: propOrderId }) => {
  const { orderId: paramOrderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  
  // Use the orderId from props or from URL params
  const effectiveOrderId = propOrderId || paramOrderId;
  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);

  // Fetch order details using GetUserOrderById API
  const loadOrder = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!effectiveOrderId) {
        throw new Error('No orderId provided');
      }
      
      const orderResp = await profileService.GetUserOrderById(effectiveOrderId);
      
      // Handle different response formats
      if ((orderResp as any).success && (orderResp as any).data) {
        setOrder((orderResp as any).data);
      } else if ((orderResp as any).success && (orderResp as any).order) {
        setOrder((orderResp as any).order);
      } else {
        // Some endpoints might return the order directly
        setOrder(orderResp as any);
      }
    } catch (e: any) {
      const errorMessage = e?.message || 'Failed to load order details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [effectiveOrderId, profileService]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Show loading state
  if (loading) {
    return (
      <div className="orderdetails-main-container" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="orderdetails-main-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="orderdetails-main-container">
      {/* Top Section - Order Info */}
      <div className="orderdetails-info-wrapper">
        <Orderinfo 
          order={order}
          onOrderCancelled={() => {
            // Reload order details after cancellation
            loadOrder();
          }}
        />
      </div>
      
      {/* Main Content Grid */}
      <div className="orderdetails-content-grid-view">
        {/* Left Section - Order Details Summary */}
        <div className="orderdetails-left-section">
          <OrderDetailsSummary order={order} />
        </div>
        
        {/* Right Section - Bill Summary */}
        <div className="orderdetails-right-section">
          <BillSummary order={order} />
        </div>
      </div>
    </div>
  );
};

export default ViewOrderDetails;